import { useState } from 'react';
import SubmitForm from './SubmitForm';

// ── Static input spec derived from inputs-kg/ ────────────────────────────────
// model_files.json
const REQUIRED_MODELS = [
  { name: 'L_Bracket.fbx', description: 'FBX model of the L-Bracket part (converted from CATIA/STEP)', format: '.fbx' },
];

// twinmaker_ids.json
const TWINMAKER_ENTITIES = [
  { id: 'BIW_Line',           role: 'Top-level BIW production line entity' },
  { id: 'Robot_Cell_1',       role: 'Robot cell entity containing fixture and robot' },
  { id: 'Fixture_001',        role: 'Fixture entity — must have ClampForce property' },
  { id: 'Robot_001',          role: 'Robot entity — must have joint state properties' },
  { id: 'CollisionAlert_001', role: 'Collision alert entity — severity property required' },
];

// constraints.json
const CONSTRAINTS = [
  { label: 'Clamp Force Min',          value: '1200 N',  rule: 'R_CAP_001' },
  { label: 'Clamp Force Max',          value: '5000 N',  rule: 'R_PART_003' },
  { label: 'Part Position Tolerance',  value: '2.0 mm',  rule: 'R_PART_001' },
  { label: 'Part Seat Tolerance',      value: '0.05 m',  rule: 'R_PART_002' },
  { label: 'Fixture Close Duration',   value: '≤ 5.0 s', rule: 'R_TIME_001' },
  { label: 'Robot Cycle Duration',     value: '≤ 5.0 s', rule: 'R_SEQ_001' },
  { label: 'Full Cycle Time',          value: '≤ 20.0 s',rule: 'R_TIME_002' },
  { label: 'Fixture Open Duration',    value: '≤ 2.0 s', rule: 'R_SEQ_003' },
];

// rules_annotations.json
const RULES = [
  { id: 'R_SEQ_001',  category: 'Sequence',   severity: 'Critical', description: 'Robot must start only after fixture is closed.' },
  { id: 'R_SEQ_002',  category: 'Sequence',   severity: 'Critical', description: 'Fixture may be considered closed only after both jaw-closed confirmations occur.' },
  { id: 'R_SEQ_003',  category: 'Sequence',   severity: 'Critical', description: 'Fixture must not open before robot returns home.' },
  { id: 'R_CAP_001',  category: 'Capability', severity: 'Major',    description: 'Clamp force must stay above minimum threshold.' },
  { id: 'R_SAFE_001', category: 'Safety',     severity: 'Critical', description: 'Nominal scenario must remain collision free.' },
  { id: 'R_SAFE_002', category: 'Safety',     severity: 'Critical', description: 'Critical collision severity must fail the run.' },
  { id: 'R_TIME_001', category: 'Timing',     severity: 'Major',    description: 'Fixture close duration shall remain within limit.' },
  { id: 'R_TIME_002', category: 'Timing',     severity: 'Major',    description: 'Cycle time shall remain within target.' },
  { id: 'R_PART_001', category: 'Part',       severity: 'Critical', description: 'Part must be detected as present before fixture close command is issued.' },
  { id: 'R_PART_002', category: 'Part',       severity: 'Critical', description: 'Part must remain clamped throughout robot operation.' },
  { id: 'R_PART_003', category: 'Part',       severity: 'Major',    description: 'Clamp force must not exceed maximum to prevent part deformation.' },
  { id: 'R_PART_004', category: 'Part',       severity: 'Major',    description: 'Part must be released cleanly after cycle completes.' },
  { id: 'R_PART_005', category: 'Part',       severity: 'Critical', description: 'Part must not collide with fixture during loading.' },
];

const SEVERITY_COLOR = { Critical: '#c0392b', Major: '#e67e22' };
const CATEGORY_COLOR = { Sequence: '#2980b9', Capability: '#8e44ad', Safety: '#c0392b', Timing: '#16a085', Part: '#27ae60' };
// ─────────────────────────────────────────────────────────────────────────────

export default function UploadPage({ onJobCreated, session }) {
  const [tab,   setTab]   = useState('requirements');
  const [rules, setRules] = useState(RULES);
  const [constraints, setConstraintsDisplay] = useState(CONSTRAINTS);

  return (
    <div style={styles.page}>
      <h2 style={{ marginBottom: 4 }}>⬆ Input Requirements &amp; Submission</h2>
      <p style={{ color: '#666', marginTop: 0, marginBottom: 16 }}>
        Review what the validation system requires before submitting your job.
      </p>

      {/* Sub-tabs */}
      <div style={styles.tabs}>
        {[['requirements', '📋 Input Requirements'], ['submit', '🚀 Submit Job']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ ...styles.tab, borderBottom: tab === key ? '3px solid #0073bb' : '3px solid transparent', fontWeight: tab === key ? 700 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── REQUIREMENTS TAB ── */}
      {tab === 'requirements' && (
        <div>

          {/* 1. Required Model Files */}
          <Section title="1. Required Model Files" icon="🗂">
            <p style={styles.hint}>These FBX files must be prepared from your CATIA/STEP source before submission.</p>
            <table style={styles.table}>
              <thead><tr>
                <Th>File Name</Th><Th>Format</Th><Th>Description</Th>
              </tr></thead>
              <tbody>
                {REQUIRED_MODELS.map(m => (
                  <tr key={m.name}>
                    <Td><code style={styles.code}>{m.name}</code></Td>
                    <Td><span style={styles.badge('#0073bb')}>{m.format}</span></Td>
                    <Td>{m.description}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 2. IoT TwinMaker Entity IDs */}
          <Section title="2. IoT TwinMaker Entity IDs" icon="🔗">
            <p style={styles.hint}>These entities must exist in your TwinMaker workspace and be synchronised with IoT SiteWise.</p>
            <table style={styles.table}>
              <thead><tr><Th>Entity ID</Th><Th>Role</Th></tr></thead>
              <tbody>
                {TWINMAKER_ENTITIES.map(e => (
                  <tr key={e.id}>
                    <Td><code style={styles.code}>{e.id}</code></Td>
                    <Td>{e.role}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 3. Validation Constraints */}
          <Section title="3. Validation Constraints" icon="⚙️">
            <p style={styles.hint}>These thresholds are fixed for this validation configuration. Contact support to adjust.</p>
            <table style={styles.table}>
              <thead><tr><Th>Constraint</Th><Th>Value</Th><Th>Enforced By</Th></tr></thead>
              <tbody>
                {CONSTRAINTS.map(c => (
                  <tr key={c.label}>
                    <Td>{c.label}</Td>
                    <Td><strong>{c.value}</strong></Td>
                    <Td><code style={styles.code}>{c.rule}</code></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 4. Validation Rules */}
          <Section title="4. Validation Rules Applied" icon="✅">
            <p style={styles.hint}>{RULES.length} rules will be evaluated. Critical failures cause an overall FAIL verdict.</p>
            <table style={styles.table}>
              <thead><tr><Th>Rule ID</Th><Th>Category</Th><Th>Severity</Th><Th>Description</Th></tr></thead>
              <tbody>
                {RULES.map(r => (
                  <tr key={r.id}>
                    <Td><code style={styles.code}>{r.id}</code></Td>
                    <Td><span style={styles.badge(CATEGORY_COLOR[r.category] || '#888')}>{r.category}</span></Td>
                    <Td><span style={styles.badge(SEVERITY_COLOR[r.severity] || '#888')}>{r.severity}</span></Td>
                    <Td style={{ fontSize: 12 }}>{r.description}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <button onClick={() => setTab('submit')} style={styles.nextBtn}>
            I have everything ready → Submit Job
          </button>
        </div>
      )}

      {/* ── SUBMIT TAB ── */}
      {tab === 'submit' && (
        <SubmitForm onJobCreated={onJobCreated} session={session} />
      )}
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Section({ title, icon, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ margin: '0 0 8px 0', color: '#0073bb' }}>{icon} {title}</h3>
      {children}
    </div>
  );
}
const Th = ({ children }) => <th style={{ textAlign: 'left', padding: '7px 10px', background: '#f0f7ff', borderBottom: '2px solid #cce', fontSize: 12 }}>{children}</th>;
const Td = ({ children, style }) => <td style={{ padding: '7px 10px', borderBottom: '1px solid #eee', verticalAlign: 'top', fontSize: 13, ...style }}>{children}</td>;

const styles = {
  page:      { maxWidth: 900, margin: '0 auto', padding: 24, fontFamily: 'sans-serif' },
  tabs:      { display: 'flex', borderBottom: '1px solid #ddd', marginBottom: 24 },
  tab:       { padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 },
  hint:      { color: '#666', fontSize: 13, margin: '0 0 10px 0' },
  table:     { width: '100%', borderCollapse: 'collapse', marginBottom: 4 },
  code:      { background: '#f4f4f4', padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace', fontSize: 12 },
  badge:     (color) => ({ background: color, color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }),
  checklist: { background: '#fffbe6', border: '1px solid #f0c040', borderRadius: 6, padding: '12px 16px', marginBottom: 16, fontSize: 13 },
  dropzone:  { border: '2px dashed', borderRadius: 8, padding: 36, textAlign: 'center', cursor: 'pointer', background: '#f9f9f9', marginBottom: 12 },
  fileList:  { listStyle: 'none', padding: 0, marginBottom: 12 },
  fileItem:  { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eee' },
  removeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#c00' },
  error:     { color: '#c00', marginTop: 8 },
  uploadBtn: { padding: '10px 24px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 15, marginBottom: 20 },
  nextBtn:   { marginTop: 8, padding: '10px 24px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  pipeline:  { background: '#f0f7ff', borderRadius: 8, padding: 14 },
  steps:     { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 },
  step:      { display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #cce', borderRadius: 6, padding: '6px 12px' },
  stepNum:   { background: '#0073bb', color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 },
};
